import { FileTransferRow } from '.prisma/client';
import { IronSessionData } from 'iron-session';
import { NextApiRequest, NextPage } from 'next';
import { WithTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import FileTranserForm from '../components/file-transfer-form';
import Layout from '../components/layout';
import { ContactPersonsOption } from '../interfaces/contact-persons';
import prisma from '../lib/prisma-client';
import { withSessionSsr } from '../lib/session';
import { mapContactPersons } from '../lib/utils/contact-persons';
import { AddFileTransferRow } from '../use-case/backend/add-file-transfer-row';
import { checkFileTransferRow } from '../use-case/backend/check-file-transfer-row';
import { createNewMaterialRow } from '../use-case/backend/new-material-row';
import { signToken } from '../use-case/backend/sign-token';

interface Props {
  rowId: string;
  csrfToken: string;
  files?: string;
  contactPersons: ContactPersonsOption[];
  materialRequestRow?: FileTransferRow;
}

const Transfer: NextPage<WithTranslation & Props> = ({
  rowId,
  csrfToken,
  files,
  contactPersons,
  materialRequestRow,
}: Props) => {
  return (
    <Layout title="transfer" backgroundImage={false}>
      <FileTranserForm
        materialRequestRow={materialRequestRow}
        contactPersons={contactPersons}
        savedFiles={files}
        rowId={rowId}
        csrfToken={csrfToken}
      ></FileTranserForm>
    </Layout>
  );
};

type GssProps = {
  req: NextApiRequest & { session: IronSessionData };
  locale: string;
};

export const getServerSideProps = withSessionSsr(async ({ req, locale }: GssProps) => {
  let rowId = await checkFileTransferRow(req.session['file-transfer-row']);
  const materialRequestRow: FileTransferRow | null = req.session['material-request-row']
    ? req.session['material-request-row']
    : null;

  if (materialRequestRow) {
    rowId = materialRequestRow.uuid;

    const checkMaterialRow = await prisma.fileTransferRow.findUnique({ where: { uuid: rowId } });

    if (checkMaterialRow && checkMaterialRow.submitted) {
      const newMaterialRow = await createNewMaterialRow(checkMaterialRow);
      if (newMaterialRow) {
        rowId = newMaterialRow.uuid;

        req.session['file-transfer-row'] = newMaterialRow.uuid;
        await req.session.save();
      }
    } else {
      req.session['file-transfer-row'] = rowId;

      await req.session.save();
    }
  }

  if (!rowId) {
    const response = await AddFileTransferRow({ userAgent: req.headers['user-agent'] || '' });
    rowId = response.uuid;
    req.session['file-transfer-row'] = rowId;

    await req.session.save();
  }

  const csrfToken = signToken(rowId + '');
  const row = await prisma.fileTransferRow.findUnique({
    where: { uuid: rowId },
    include: { files: true },
  });
  const files = row?.files ? JSON.stringify(row?.files) : null;

  const contactPersons = await prisma.contactPerson.findMany({ where: { show: true } });
  req.session.gdpr = false;

  await req.session.save();
  const mappedContactPersons = mapContactPersons(contactPersons);
  return {
    props: {
      materialRequestRow,
      csrfToken,
      rowId,
      files,
      contactPersons: mappedContactPersons,
      ...(await serverSideTranslations(locale, [
        'common',
        'form',
        'validation',
        'error-box',
        'recaptcha',
      ])),
    },
  };
});

export default Transfer;
